/* eslint-disable @typescript-eslint/ban-ts-comment */
import Dexie, { Table, EntityTable } from 'dexie'

type Contact = {
  id: string
  name: string
  email: string
  type: string
  extension: string
  phone: string
  phones: string[]
  avatar: string
  status: 'active' | 'deleted'
}

// this is Indexed DB lightweight wrapper
const db = new Dexie('ContactsDatabase') as Dexie & {
  Contacts: EntityTable<Contact, 'id'>
  ContactsSyncSettings: Table<string, string>
}

// Schema declaration:
db.version(1).stores({
  Contacts: 'id',
  ContactsSyncSettings: '',
})

const SYNC_TOKEN_KEY = 'sync_token'

const SYNC_TOKEN_USER_KEY = 'sync_token_user'

export const getAllIndexDbContacts = async (): Promise<Contact[]> => {
  return (await db.Contacts?.toArray()) ?? []
}

export const getSyncToken = (): Promise<string | undefined> => {
  return db.ContactsSyncSettings.get(SYNC_TOKEN_KEY)
}

export const setUserKeySyncToken = (
  userId: string,
): Promise<string | undefined> => {
  return db.ContactsSyncSettings.put(userId, SYNC_TOKEN_USER_KEY)
}

export const getUserKeySyncToken = (): Promise<string | undefined> => {
  return db.ContactsSyncSettings.get(SYNC_TOKEN_USER_KEY)
}

export const setSyncToken = async (value: string): Promise<void> => {
  await db.ContactsSyncSettings.put(value, SYNC_TOKEN_KEY).catch(() => {
    // eslint-disable-next-line no-console
    console.error('Cannot store sync_token in Indexed DB')
  })
}

export const syncContactsWithIndexDb = async (
  contactsDiff: Contact[],
): Promise<void> => {
  const { updatedContacts, deletedContactIds } = contactsDiff.reduce<{
    updatedContacts: Contact[]
    deletedContactIds: string[]
  }>(
    (acc, contact) => {
      if (contact.status === 'deleted') {
        acc.deletedContactIds.push(contact.id)
      } else {
        acc.updatedContacts.push(contact)
      }

      return acc
    },
    { updatedContacts: [], deletedContactIds: [] },
  )

  if (updatedContacts.length) {
    await db.Contacts.bulkPut(updatedContacts).catch(() => {
      // eslint-disable-next-line no-console
      console.error('Cannot store contacts in Indexed DB')
      db.ContactsSyncSettings.delete(SYNC_TOKEN_KEY)
    })
  }

  if (deletedContactIds.length) {
    await db.Contacts.bulkDelete(deletedContactIds).catch(() => {
      // eslint-disable-next-line no-console
      console.error('Cannot delete contacts in Indexed DB')
      db.ContactsSyncSettings.delete(SYNC_TOKEN_KEY)
    })
  }
}

export const getContactDetailsFromIndexDb = async (
  id: string,
): Promise<Contact | null> => {
  try {
    const contact = await db.Contacts.get(id)

    return contact || null
  } catch (err) {
    return null
  }
}

export const clearContactsDb = (): Promise<any> => {
  return Promise.allSettled([
    db.ContactsSyncSettings.delete(SYNC_TOKEN_KEY),
    db.Contacts.clear(),
  ])
}
